import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TablePagination from "@mui/material/TablePagination";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { listClientJobs, listTasks, listTenantUsers, updateClientJob, updateTask } from "services/tenantUserAdminService";
import { STATUS_CFG } from "../../../constants/jobStatus";
import { getServiceLabel } from "../../../constants/serviceLabels";
import { TC, FS, FW } from "../../../tokens/text";

function derivedStatus(job) {
  const { status, completedCount = 0, taskCount = 0, dueDate } = job;
  if (status === "cancelled") return "cancelled";
  if (status === "invoiced")  return "invoiced";
  if (status === "filed")     return "filed";
  if (taskCount > 0 && completedCount >= taskCount) return "filed";
  if (status === "approved")  return "approved";
  if (status === "review")    return "review";
  if (dueDate) {
    const today = new Date(); today.setHours(0,0,0,0);
    if (new Date(dueDate) < today) return "overdue";
  }
  if (completedCount > 0) return "in_progress";
  return "pending";
}

function dueDateRowStyle(dueDate) {
  if (!dueDate) return {};
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days  = Math.ceil((new Date(dueDate) - today) / 86400000);
  if (days < 0)   return { background: "rgba(211,47,47,0.06)",  borderLeft: "3px solid rgba(211,47,47,0.55)"  };
  if (days <= 14) return { background: "rgba(255,109,0,0.06)",  borderLeft: "3px solid rgba(255,109,0,0.5)"   };
  if (days <= 30) return { background: "rgba(255,152,0,0.05)",  borderLeft: "3px solid rgba(255,152,0,0.45)"  };
  return              { background: "rgba(56,142,60,0.04)",  borderLeft: "3px solid rgba(56,142,60,0.35)"  };
}

const SERVICE_TAB_INDEX = {
  payroll:             0,
  vat:                 1,
  accounts:            2,
  company_secretarial: 3,
  ad01_address_change: 4,
};

const TASK_TYPE_TAB_INDEX = {
  YEAR_END:               2,
  CONFIRMATION_STATEMENT: 3,
  ACCOUNTS_PREPARATION:   2,
  CT600:                  2,
  VAT_RETURN:             1,
  PAYROLL:                0,
};

const ROWS_PER_PAGE = 10;

function dueDaysFromNow(dueDateStr) {
  if (!dueDateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.floor((new Date(dueDateStr) - today) / 86400000);
}

function DueChip({ dueDate }) {
  const days = dueDaysFromNow(dueDate);
  if (days === null) return <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm }}>—</VuiTypography>;
  if (days < 0)
    return <Chip label={`${Math.abs(days)}d overdue`} size="small" sx={{ bgcolor: "rgba(244,67,54,0.18)", color: TC.error, fontSize: "0.62rem", height: 18, fontWeight: FW.bold }} />;
  if (days <= 7)
    return <Chip label={`${days}d left`} size="small" sx={{ bgcolor: "rgba(255,152,0,0.18)", color: TC.warning, fontSize: "0.62rem", height: 18, fontWeight: FW.bold }} />;
  return (
    <VuiTypography sx={{ color: TC.midtone, fontSize: FS.sm }}>
      {new Date(dueDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
    </VuiTypography>
  );
}

export default function UpcomingJobsWidget({ tenantId }) {
  const history = useHistory();
  const [jobs,    setJobs]    = useState([]);
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(0);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([listClientJobs(tenantId, null), listTasks(tenantId, {}), listTenantUsers({ tenantId })])
      .then(([jobsRes, tasksRes, usersRes]) => {
        setUsers(usersRes.users || []);
        const now    = new Date(); now.setHours(0, 0, 0, 0);
        const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() + 90);

        const taskRows = (tasksRes.tasks || []).map(t => ({
          ...t,
          _isTask:        true,
          serviceId:      null,
          completedCount: 0,
          taskCount:      0,
        }));

        const filtered = [...(jobsRes.jobs || []), ...taskRows]
          .filter((j) => {
            if (j.status === "filed" || j.status === "completed" || j.status === "invoiced" || j.status === "cancelled") return false;
            if (!j.dueDate) return false;
            return new Date(j.dueDate) <= cutoff;
          })
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        setJobs(filtered);
      })
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const pageJobs = jobs.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  function handleAssignUpdated(jobId, uid, name) {
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, assignedTo: uid, assignedName: name } : j));
  }

  const headerCell = (label, sx) => (
    <VuiBox sx={{ overflow: "hidden", display: "flex", justifyContent: "center", ...sx }}>
      <VuiTypography sx={{ color: TC.secondary, fontSize: FS.xs, fontWeight: FW.bold,
        textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap", textAlign: "center" }}>
        {label}
      </VuiTypography>
    </VuiBox>
  );

  return (
    <VuiBox display="flex" flexDirection="column" height="100%">
      {/* Sub-header with count */}
      <VuiBox px={2.5} pt={1.5} pb={1} display="flex" alignItems="center" justifyContent="space-between" flexShrink={0}>
        <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm }}>
          Due within the next 3 months
        </VuiTypography>
        {!loading && (
          <Chip
            label={`${jobs.length} job${jobs.length !== 1 ? "s" : ""}`}
            size="small"
            sx={{ bgcolor: "rgba(0,117,255,0.15)", color: TC.link, fontSize: "0.68rem", border: "1px solid rgba(0,117,255,0.3)" }}
          />
        )}
      </VuiBox>

      {/* Column headers */}
      <VuiBox display="flex" alignItems="center" gap={1.5} px={2.5} py={1} flexShrink={0}
        sx={{ borderBottom: "1px solid #b0c8e0" }}>
        {headerCell("Job Title",  { flex: "1 1 160px" })}
        {headerCell("Client",     { flex: "1 1 140px" })}
        {headerCell("Assigned",   { width: 130, minWidth: 100, flexShrink: 0 })}
        {headerCell("Due",        { width: 110, minWidth: 80,  flexShrink: 0 })}
        {headerCell("Progress",   { width: 110, minWidth: 90,  flexShrink: 0 })}
        {headerCell("Status",     { width: 90,  minWidth: 80,  flexShrink: 0 })}
        <VuiBox sx={{ width: 32, flexShrink: 0 }} />
      </VuiBox>

      {/* Content */}
      <VuiBox sx={{ flex: 1, overflowY: "auto", scrollbarGutter: "stable", minHeight: 0 }}>
        {loading ? (
          <VuiBox display="flex" justifyContent="center" py={5}><CircularProgress size={28} /></VuiBox>
        ) : jobs.length === 0 ? (
          <VuiBox py={5} textAlign="center">
            <VuiTypography sx={{ color: TC.secondary, fontSize: FS.md }}>
              No jobs due in the next 3 months 🎉
            </VuiTypography>
          </VuiBox>
        ) : (
          <>
            {pageJobs.map((job) => {
              const cfg    = STATUS_CFG[derivedStatus(job)] || {};
              const pct    = job.taskCount > 0 ? Math.round((job.completedCount / job.taskCount) * 100) : 0;
              const jobTab = job._isTask
                ? (TASK_TYPE_TAB_INDEX[job.type] ?? 3)
                : (SERVICE_TAB_INDEX[job.serviceId] ?? 0);
              return (
                <VuiBox
                  key={job.id}
                  display="flex" alignItems="center" gap={1.5} px={2.5} py={1.2}
                  onClick={() => history.push(`/client-profiles/${job.clientId}`, { tab: 7, jobTab })}
                  sx={{
                    ...dueDateRowStyle(job.dueDate),
                    borderBottom: "1px solid #b0c8e0",
                    cursor: "pointer",
                    "&:hover": { background: "rgba(0,117,255,0.06)" },
                    transition: "background 0.15s",
                  }}
                >
                  <VuiBox sx={{ flex: "1 1 160px", overflow: "hidden", display: "flex", justifyContent: "center" }}>
                    <VuiTypography sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.semibold,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>
                      {job.title || getServiceLabel(job.serviceId)}
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox sx={{ flex: "1 1 140px", overflow: "hidden", display: "flex", justifyContent: "center" }}>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>
                      {job.clientName || job.clientId}
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox sx={{ width: 130, minWidth: 100, flexShrink: 0, display: "flex", justifyContent: "center" }} onClick={e => e.stopPropagation()}>
                    <Select value={job.assignedTo || ""} size="small" displayEmpty
                      onChange={async (e) => {
                        const uid = e.target.value;
                        const user = users.find(u => (u.uid || u.id) === uid);
                        const assignedName = uid === "" ? null : (user?.displayName || user?.email || uid);
                        const payload = { assignedTo: uid === "" ? null : uid, assignedName };
                        if (job._isTask) await updateTask(tenantId, job.id, payload);
                        else             await updateClientJob(tenantId, job.id, payload);
                        handleAssignUpdated(job.id, uid === "" ? null : uid, assignedName);
                      }}
                      sx={{
                        color: job.assignedTo ? "#0057d8" : TC.tertiary,
                        fontSize: "0.72rem", fontWeight: job.assignedTo ? 600 : 400,
                        background: job.assignedTo ? "rgba(0,87,216,0.08)" : "#f4f8ff",
                        border: job.assignedTo ? "1px solid rgba(0,87,216,0.4)" : "1px solid #d8e6f7",
                        borderRadius: "6px", height: 26, minWidth: 110, maxWidth: 140,
                        ".MuiSelect-icon": { color: job.assignedTo ? "#0057d8" : TC.secondary, fontSize: "1rem" },
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                      }}>
                      <MenuItem value="" sx={{ fontSize: "0.75rem", color: TC.secondary, fontStyle: "italic" }}>Unassigned</MenuItem>
                      {users.map(u => {
                        const uid = u.uid || u.id;
                        return <MenuItem key={uid} value={uid} sx={{ fontSize: "0.75rem" }}>{u.displayName || u.email || uid}</MenuItem>;
                      })}
                    </Select>
                  </VuiBox>
                  <VuiBox sx={{ width: 110, minWidth: 80, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                    <DueChip dueDate={job.dueDate} />
                  </VuiBox>
                  <VuiBox sx={{ width: 110, minWidth: 90, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}>
                    <VuiBox sx={{ flex: 1 }}>
                      <LinearProgress variant="determinate" value={pct} sx={{
                        height: 4, borderRadius: 3,
                        bgcolor: "#7a94b0",
                        "& .MuiLinearProgress-bar": { bgcolor: pct === 100 ? "#4caf50" : "#2196f3", borderRadius: 3 },
                      }} />
                    </VuiBox>
                    <VuiTypography sx={{ color: TC.tertiary, fontSize: FS.sm, whiteSpace: "nowrap" }}>
                      {job.completedCount || 0}/{job.taskCount || 0}
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox sx={{ width: 90, minWidth: 80, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                    <VuiBox display="inline-flex" px={0.8} py={0.3}
                      sx={{ background: `${cfg.color}18`, border: `1px solid ${cfg.color}55`, borderRadius: "6px" }}>
                      <VuiTypography sx={{ color: cfg.color, fontSize: FS.xs, fontWeight: FW.bold, whiteSpace: "nowrap" }}>
                        {cfg.label || job.status}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>
                  <VuiBox sx={{ width: 32, flexShrink: 0 }}>
                    <IconButton size="small"
                      onClick={(e) => { e.stopPropagation(); history.push(`/client-profiles/${job.clientId}`, { tab: 7, jobTab }); }}
                      sx={{ p: 0.3, color: TC.tertiary, "&:hover": { color: TC.linkHover } }}>
                      <OpenInNewIcon sx={{ fontSize: FS.md }} />
                    </IconButton>
                  </VuiBox>
                </VuiBox>
              );
            })}
          </>
        )}
      </VuiBox>

      {/* Pagination outside scroll area */}
      {!loading && jobs.length > ROWS_PER_PAGE && (
        <TablePagination
          component="div"
          count={jobs.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          sx={{
            flexShrink: 0,
            color: TC.secondary,
            fontSize: FS.sm,
            "& .MuiTablePagination-actions button": { color: TC.secondary },
            "& .MuiTablePagination-actions button:hover": { color: "#fff" },
            "& .MuiTablePagination-actions button.Mui-disabled": { color: TC.tertiary },
          }}
        />
      )}
    </VuiBox>
  );
}


